import {
  SkyButton,
  SkyDialog,
  SkyDropdown,
  SkyInput,
  SkyMessageBar,
  SkyTabList,
  SkyText,
} from "@skypointcloud/skypoint-ui";
import type { TabItem } from "@skypointcloud/skypoint-ui";
import type { AuthIssuerView } from "@shared/types/accessAdmin";
import { useMemo, useState, type ReactElement, type ReactNode } from "react";
import { AccessFilters } from "@/components/access/AccessFilters";
import {
  ChangeRoleDialog,
  type ChangeRoleChoice,
  type ChangeRoleTarget,
} from "@/components/access/ChangeRoleDialog";
import { DeactivateUserDialog } from "@/components/access/DeactivateUserDialog";
import { DirectoryGroupsPanel } from "@/components/access/DirectoryGroupsPanel";
import { SIGN_IN_WAIT_LABEL, signInWait } from "@/components/access/signInWait";
import { GrantAccessDialog, type GrantRoleChange } from "@/components/access/GrantAccessDialog";
import {
  ReactivateAccessDialog,
  type ReactivateTarget,
} from "@/components/access/ReactivateAccessDialog";
import { RevokeGrantDialog } from "@/components/access/RevokeGrantDialog";
import {
  EmptyState,
  ErrorState,
  LoadingRows,
  PermissionNotice,
} from "@/components/access/ScreenStates";
import { UsersGrid, grantScopeLabel, type ScopeNames } from "@/components/access/UsersGrid";
import { TopbarActionsPortal } from "@/components/navigation/TopbarActionsPortal";
import { ReauthDialog } from "@/components/settings/ReauthDialog";
import { useTenantInstance } from "@/contexts/TenantInstanceContext";
import {
  issuerDisplay,
  issuerOption,
  scopeForRole,
  useAccessMutations,
  useAccessUsers,
  useAuthIssuers,
  useGrantSignInSetting,
  type AccessListFilters,
  type AccessPerson,
  type AccessRow,
  type AccessScope,
  type GrantInput,
  type RebindInput,
} from "@/hooks/use-access";
import {
  useGroupAccessEnabled,
  useGroupAccessMutations,
  useReauthRetry,
} from "@/hooks/use-group-access";
import { InlineErrorBar, useInlineError, type InlineFailure } from "@/lib/feedback/InlineErrorBar";
import { useNotice } from "@/lib/feedback/NoticeProvider";
import { useApiErrorNotice } from "@/lib/feedback/useApiErrorNotice";
import { useNotificationRules } from "@/hooks/use-notifications";
import { usePermissions } from "@/hooks/use-permissions";
import { AddIcon } from "@/lib/icons";
import { USERS_DENIED_MESSAGE } from "@/lib/routes";
import { ApiClientError } from "@/lib/api";
import { ROLE_LABELS, ROLE_NAMES } from "@shared/permissions";
import type { OverrideRole, PermissionKey, RoleName } from "@shared/permissions";
import { adminNeedsSsoChoice, isMfaAdminRole } from "@shared/types/mfa";
import { RefreshErrorBar } from "@/components/states/RefreshErrorBar";

/** The "Directory groups" tab (group access): not a list scope, so it is its own value. */
const GROUPS_TAB = "groups";

/** The permission that governs WRITES at each tab: the three surfaces check three keys. */
const MANAGE_PERMISSION: Record<AccessScope, PermissionKey> = {
  instance: "instance.users.manage",
  tenant: "tenant.users.manage",
  platform: "platform.tenants.manage",
};

/** #1275: the roles a `ROLE_CHANGE_REQUIRED` refusal names as the person's current ones. */
function currentRolesOf(cause: ApiClientError): RoleName[] {
  return cause.details
    .filter((detail) => detail.field === "currentRole")
    .map((detail) => detail.issue)
    .filter((role): role is RoleName => (ROLE_NAMES as readonly string[]).includes(role));
}

function scopeNameOf(scope: AccessScope, tenantName: string, instanceName: string): string {
  if (scope === "platform") return "Platform";
  return scope === "tenant" ? tenantName : instanceName;
}

/** The grant whose row action is in flight, so its button shows the spinner. */
function busyGrantIdOf(mutations: ReturnType<typeof useAccessMutations>): string | null {
  if (mutations.revoke.isPending) return mutations.revoke.variables?.grantId ?? null;
  if (mutations.reactivate.isPending) return mutations.reactivate.variables?.grantId ?? null;
  if (mutations.setUserActive.isPending) {
    return mutations.setUserActive.variables?.row.grantId ?? null;
  }
  if (mutations.resendInvite.isPending) return mutations.resendInvite.variables?.grantId ?? null;
  return null;
}

/** The scope tabs, only when there is more than one surface to choose. */
function AccessTabStrip({
  tabs,
  selectedValue,
  onSelect,
}: {
  tabs: TabItem[];
  selectedValue: string;
  onSelect: (value: string) => void;
}) {
  if (tabs.length <= 1) return null;
  return (
    <SkyTabList
      tabs={tabs}
      selectedValue={selectedValue}
      testId="access-scope-tabs"
      onTabSelect={(value) => onSelect(value)}
    />
  );
}

/** The tab says "This tenant"; its first sentence names it (round 6 A3). */
function TenantScopeIntro({ tenantName }: { tenantName: string }) {
  return (
    <SkyText as="p" size={200} color="muted" block testId="users-scope-intro">
      {`People who can reach every instance of ${tenantName}: access granted on the tenant, and each instance's own grants.`}
    </SkyText>
  );
}

/** The people at this tab: loading, the read's failure, only you, or the grid. */
function PeopleResults({
  isPending,
  isError,
  hasData,
  error,
  onRetry,
  empty,
  scopeName,
  onGrant,
  renderGrid,
}: {
  isPending: boolean;
  isError: boolean;
  /** A list was read before: a failed re-read keeps it under a warning (#1398). */
  hasData: boolean;
  error: unknown;
  onRetry: () => void;
  empty: boolean;
  scopeName: string;
  onGrant: (() => void) | undefined;
  renderGrid: () => ReactElement;
}) {
  if (isPending) return <LoadingRows rows={5} columns={6} />;
  if (isError && !hasData) {
    return <ErrorState title="The user list did not load" error={error} onRetry={onRetry} />;
  }
  return (
    <>
      {isError ? (
        <RefreshErrorBar
          things="people"
          error={error}
          onRetry={onRetry}
          empty={empty}
          testId="users-refresh-error"
        />
      ) : null}
      {empty ? <OnlyYou scopeName={scopeName} onGrant={onGrant} /> : renderGrid()}
    </>
  );
}

/** Nobody else can reach the scope yet: Grant access, for a reader who may. */
function OnlyYou({ scopeName, onGrant }: { scopeName: string; onGrant: (() => void) | undefined }) {
  return (
    <EmptyState
      title={`Only you can reach ${scopeName}`}
      message="Grant access by entering an email address and choosing a role. A person who has not signed in yet appears here as Pending until their first sign-in."
      action={
        onGrant ? (
          <SkyButton appearance="primary" icon={AddIcon} onClick={onGrant}>
            Grant access
          </SkyButton>
        ) : undefined
      }
    />
  );
}

/**
 * Users and access (canvas 07; FR-11, FR-13, SEC-18).
 *
 * The scope tabs are the screen's spine: one grid, one grant dialog and one
 * revoke confirmation serve this instance, its tenant and the platform, because
 * all three surfaces answer the same user view. A tab appears only when the
 * signed-in user holds the permission that surface checks — and the route guard
 * in `App.tsx` already refuses the screen itself without `instance.users.read`,
 * so hiding a tab is a courtesy on top of a control rather than instead of one.
 */
export default function AdminUsersPage() {
  const { can, canAny, isResolved } = usePermissions();
  const { selectedInstance, me } = useTenantInstance();
  const notice = useNotice();
  // A row action's failure (resend): its control is a row button, so the notice. Reactivate and
  // Activate user confirm in a dialog first (#2091) and say a refusal inside it.
  const showError = useApiErrorNotice();
  // The three dialogs stay open on a failure and say it inside (ux-s10 §3).
  const grantError = useInlineError();
  const revokeError = useInlineError();
  const rebindError = useInlineError();
  // #418 review: the server's reason for refusing the chosen provider, shown at the dropdown.
  const [rebindIssuerIssue, setRebindIssuerIssue] = useState<string | null>(null);

  const instanceName = selectedInstance?.name ?? "this instance";
  // The name people read is the tenant's Display name (platform 0025: a tenant has none).
  const tenantName = selectedInstance?.tenantDisplayName ?? "this tenant";
  const grantorRole = selectedInstance?.roleName ?? "Viewer";

  const groupAccess = useGroupAccessEnabled();
  const showGroups = groupAccess && can("tenant.users.manage");

  const tabs = useMemo<TabItem[]>(() => {
    const items: TabItem[] = [{ value: "instance", label: "This instance" }];
    // "This tenant", as the Audit trail's tab strip says (round 6 A3); the panel names it.
    if (can("tenant.users.manage")) items.push({ value: "tenant", label: "This tenant" });
    if (can("platform.tenants.manage")) items.push({ value: "platform", label: "Platform" });
    // Group access (2026-09-28): the tenant's directory groups, beside the list tabs.
    if (showGroups) items.push({ value: GROUPS_TAB, label: "Directory groups" });
    return items;
  }, [can, showGroups]);

  const [scope, setScope] = useState<AccessScope>("instance");
  const [onGroupsTab, setOnGroupsTab] = useState(false);
  const groupMutations = useGroupAccessMutations(selectedInstance?.tenantId ?? null);
  const overrideRetry = useReauthRetry();
  // Admin MFA (owner answer W3-12): grants, revokes, reactivations, status changes and rebinds
  // need a recent sign-in, and a second factor with an MFA mode on. A refusal holds the write in
  // the re-authentication dialog and replays it once identity is confirmed.
  const accessRetry = useReauthRetry();
  const changeRoleError = useInlineError();
  const [changingRole, setChangingRole] = useState<ChangeRoleTarget | null>(null);
  const [filters, setFilters] = useState<AccessListFilters>({ role: "all", status: "all" });

  // Every instance and tenant the viewer's `/api/v2/me` names, so a chip names
  // the scope its grant was made on rather than the one selected.
  const names = useMemo<ScopeNames>(() => {
    const instances = new Map<string, { name: string; tenantName: string }>();
    const tenants = new Map<string, string>();
    for (const instance of me?.instances ?? []) {
      // The Display name, which people read (`tenantName` is the tenant's fixed Name).
      instances.set(instance.id, { name: instance.name, tenantName: instance.tenantDisplayName });
      tenants.set(instance.tenantId, instance.tenantDisplayName);
    }
    return { instances, tenants };
  }, [me?.instances]);

  function openScope(next: AccessScope) {
    setOnGroupsTab(false);
    setScope(next);
    setFilters({ role: "all", status: "all" });
  }

  function openTab(value: string) {
    if (value === GROUPS_TAB) {
      setOnGroupsTab(true);
      return;
    }
    openScope(value as AccessScope);
  }
  const [isGrantOpen, setGrantOpen] = useState(false);
  // #1275: a grant refused because the person already holds another role here, held for confirm.
  const [roleChange, setRoleChange] = useState<GrantRoleChange | null>(null);
  const [revoking, setRevoking] = useState<AccessRow | null>(null);
  const [resetting, setResetting] = useState<AccessPerson | null>(null);
  const [deactivating, setDeactivating] = useState<AccessRow | null>(null);
  const deactivateError = useInlineError();
  // #2091: Reactivate and Activate user confirm first, naming the person, as Revoke does.
  const [reactivating, setReactivating] = useState<ReactivateTarget | null>(null);
  const reactivateError = useInlineError();

  // #1067: Instance admin and above. Below it nothing is asked for, the notice below says why.
  const mayRead = isResolved && canAny("instance.users.read");
  const { people, data, isPending, isError, error, refetch } = useAccessUsers(
    scope,
    filters,
    mayRead,
  );
  // Product gaps row 19: whether a grant sends the invitation email, read only while the grant
  // dialog is open, from the status the notifications view already reports.
  const emailStatus = useNotificationRules(isGrantOpen);
  // #1639 C0: which sign-in methods the dialog offers, read only while it is open.
  const signInSetting = useGrantSignInSetting(isGrantOpen);
  const mutations = useAccessMutations(scope);

  // Session 9: which identity providers the API trusts. Only the platform surface can rebind a
  // person or choose the provider a grant is pinned to, but a platform administrator reads the
  // list on every tab (fix/grant-flow): it says where a PlatformAdmin grant pins, which the
  // instance tab's dialog states too. Nobody else calls it, so no issuer detail reaches them.
  const isPlatformAdministrator = can("platform.tenants.manage");
  const onPlatformTab = scope === "platform" && isPlatformAdministrator;
  const issuersQuery = useAuthIssuers(isPlatformAdministrator);
  const issuers = useMemo(() => issuersQuery.data ?? [], [issuersQuery.data]);
  // #1660 review: a person already on this list keeps the method they sign in with.
  const knownSignInMethod = useMemo(
    () => (address: string) => {
      const person = people.find((candidate) => candidate.email.toLowerCase() === address);
      if (!person || (!person.idpIssuer && !person.expectedIdpIssuer)) return undefined;
      return person.signInMethod ?? null;
    },
    [people],
  );

  // The permission that governs WRITES at the tab in view. The three surfaces
  // check three different keys, so the button cannot read one of them and hope.
  const canManage = can(MANAGE_PERMISSION[scope]);

  // Until `/api/v2/me` answers, nothing is known about this user's permissions.
  // Rendering the denial first would flash "you do not have access" at somebody
  // who does, so the screen waits rather than guessing.
  if (!isResolved) return <LoadingRows rows={5} columns={6} />;

  if (!mayRead) {
    return <PermissionNotice permission="instance.users.read" message={USERS_DENIED_MESSAGE} />;
  }

  const scopeName = scopeNameOf(scope, tenantName, instanceName);

  // Each confirmation is posted after its dialog closes, so it is not inside the modal's hidden
  // window; the toast's `content` (which hid the message) is folded into one sentence.
  /**
   * Runs one access write through the re-authentication hold: `done` runs when it lands, now or
   * after the sign-in; `failed` gets any other refusal. A held write reports nothing yet.
   */
  function guarded<T>(
    write: () => Promise<T>,
    done: (result: T) => void,
    failed: (cause: unknown) => void,
  ) {
    accessRetry
      .run<T>(write, done)
      .then((result) => {
        if (result === null) return;
        done(result);
      })
      .catch(failed);
  }

  function handleGrant(input: GrantInput) {
    grantError.clear();
    guarded(
      () => mutations.grant.mutateAsync(input),
      (granted) => {
        setGrantOpen(false);
        setRoleChange(null);
        // B-55: a deactivated person gets no invitation, whatever the toast used to promise.
        if (granted.inviteOutcome === "user_disabled") {
          notice.warning(
            `${input.email} is deactivated, so no invitation was sent and they cannot sign in. A platform administrator must activate the account first.`,
            { title: "Access granted, account deactivated" },
          );
          return;
        }
        // #1639 C0: an Email grant waits for the sign-in account Skypoint creates.
        if (granted.inviteOutcome === "account_pending") {
          notice.info(
            `${input.email} signs in with Email. No invitation was sent yet. Ask Skypoint support to create their sign-in account, then use Resend invitation. They appear as Pending until their first sign-in.`,
            { title: "Access granted, sign-in account needed" },
          );
          return;
        }
        // #1275: Pending only for a person who is pending. An older server sends no status.
        if (granted.userStatus === "active") {
          const where = scopeNameOf(scopeForRole(input.roleName), tenantName, instanceName);
          notice.success(`${input.email} now has ${ROLE_LABELS[input.roleName]} on ${where}.`, {
            title: "Access granted",
          });
          return;
        }
        notice.success(`${input.email} appears as Pending until their first sign-in.`, {
          title: "Access granted",
        });
      },
      (cause) => {
        // #1275: one role per person per scope. The dialog asks to change the current role.
        if (cause instanceof ApiClientError && cause.code === "ROLE_CHANGE_REQUIRED") {
          setRoleChange({ input, currentRoles: currentRolesOf(cause) });
          return;
        }
        grantError.show(cause, "The grant could not be saved.");
      },
    );
  }

  function handleRevoke(row: AccessRow) {
    revokeError.clear();
    guarded(
      // A revoke answers 204, which resolves to undefined: map it so "held" (null) stays distinct.
      () => mutations.revoke.mutateAsync(row).then(() => true),
      () => {
        setRevoking(null);
        notice.success(`${row.email} keeps their history.`, { title: "Access revoked" });
      },
      (cause) => revokeError.show(cause, "The access could not be revoked."),
    );
  }

  function handleReactivate(row: AccessRow) {
    reactivateError.clear();
    guarded(
      () => mutations.reactivate.mutateAsync(row),
      () => {
        setReactivating(null);
        notice.success(row.email, { title: "Access reactivated" });
      },
      (cause) => reactivateError.show(cause, "The access could not be reactivated."),
    );
  }

  function handleRebind(input: RebindInput, person: AccessPerson) {
    rebindError.clear();
    setRebindIssuerIssue(null);
    guarded(
      () => mutations.rebind.mutateAsync(input),
      () => {
        setResetting(null);
        notice.success(
          person.isSelf
            ? "Sign in again through the provider you chose."
            : `${person.email} activates the account at their next sign-in through ${issuerDisplay(input.toIssuer, issuers).text}.`,
          { title: "Sign-in reset" },
        );
      },
      (cause) => {
        setRebindIssuerIssue(toIssuerIssueOf(cause));
        rebindError.show(cause, "The sign-in could not be reset.");
      },
    );
  }

  function handleResendInvite(row: AccessRow) {
    guarded(
      () => mutations.resendInvite.mutateAsync(row),
      (resent) => {
        // #1678: the invitation waits for the account. Review of #1684: claims no creation under
        // way, since a runner with no writer closes the request and the list then says so.
        if (resent?.held) {
          notice.info(
            `${row.email} gets the invitation once their sign-in account exists. The list shows if it cannot be made.`,
            { title: "Invitation waiting for account" },
          );
          return;
        }
        // #1678 (#1676): sent, but nobody here knows whether Skypoint has made the account yet.
        if (resent?.accountUnconfirmed) {
          notice.info(
            `${row.email} gets a new invitation email. They sign in with Email. If they cannot sign in, ask Skypoint support to confirm their sign-in account exists.`,
            { title: "Invitation resent" },
          );
          return;
        }
        notice.success(`${row.email} gets a new invitation email with the sign-in link.`, {
          title: "Invitation resent",
        });
      },
      (cause) => showError(cause, "The invitation could not be resent."),
    );
  }

  function handleActivateUser(row: AccessRow) {
    reactivateError.clear();
    guarded(
      () => mutations.setUserActive.mutateAsync({ row, active: true }),
      () => {
        setReactivating(null);
        notice.success(row.email, { title: "Account activated" });
      },
      (cause) => reactivateError.show(cause, "The account could not be activated."),
    );
  }

  function handleConfirmReactivate(target: ReactivateTarget) {
    if (target.kind === "account") handleActivateUser(target.row);
    else handleReactivate(target.row);
  }

  function handleDeactivateUser(row: AccessRow, reason: string) {
    deactivateError.clear();
    guarded(
      () => mutations.setUserActive.mutateAsync({ row, active: false, reason }),
      () => {
        setDeactivating(null);
        notice.success(
          `${row.email} can no longer sign in or reach anything. Activate user puts it back.`,
          { title: "Account deactivated" },
        );
      },
      (cause) => deactivateError.show(cause, "The account could not be deactivated."),
    );
  }

  // Group access: "Change role" raises a group member (the override) or sets them back.
  function handleChangeRole(choice: ChangeRoleChoice, target: ChangeRoleTarget) {
    changeRoleError.clear();
    const userId = target.person.userId;
    const write =
      choice === "group"
        ? () => groupMutations.clearOverride.mutateAsync({ userId })
        : () =>
            groupMutations.setOverride.mutateAsync({ userId, roleName: choice as OverrideRole });
    const changed = () => {
      setChangingRole(null);
      notice.success(
        choice === "group"
          ? `${target.person.email} has the role of ${target.groupLabel} again.`
          : `${target.person.email} works with the new role while in ${target.groupLabel}.`,
        { title: "Role changed" },
      );
    };
    // `run` resolves to null when the write was held for a re-authentication: nothing was
    // written, so nothing is reported. A 204 (setting the group role back) resolves to undefined.
    // The held write reports through `changed` only once it has landed after the sign-in.
    overrideRetry
      .run<unknown>(write, changed)
      .then((done) => {
        if (done === null) return;
        changed();
      })
      .catch((cause: unknown) => changeRoleError.show(cause, "The role was not changed."));
  }

  function openChangeRole(person: AccessPerson, groupGrant: AccessRow) {
    const override = person.grants.find(
      (grant) => grant.source === "override" && grant.isActive && grant.scopeType === "instance",
    );
    setChangingRole({
      person,
      groupRole: groupGrant.roleName as RoleName,
      groupLabel: groupGrant.mappingLabel ?? "the group",
      current: override ? (override.roleName as OverrideRole) : null,
    });
  }

  const busyGrantId = busyGrantIdOf(mutations);

  return (
    <div className="space-y-4">
      {canManage && !onGroupsTab ? (
        <TopbarActionsPortal>
          <SkyButton
            appearance="primary"
            icon={AddIcon}
            testId="grant-access"
            onClick={() => setGrantOpen(true)}
          >
            Grant access
          </SkyButton>
        </TopbarActionsPortal>
      ) : null}

      <AccessTabStrip
        tabs={tabs}
        selectedValue={onGroupsTab ? GROUPS_TAB : scope}
        onSelect={openTab}
      />

      {onGroupsTab && showGroups && selectedInstance ? (
        <DirectoryGroupsPanel
          tenantId={selectedInstance.tenantId}
          tenantName={tenantName}
          grantorRole={grantorRole}
          instances={(me?.instances ?? []).filter(
            (instance) => instance.tenantId === selectedInstance.tenantId,
          )}
        />
      ) : (
        <>
          {scope === "tenant" ? <TenantScopeIntro tenantName={tenantName} /> : null}
          <SkyMessageBar
            intent="info"
            layout="multiline"
            title="Access here is granted directly"
            message="Enter an email address and pick a role. A person who has not signed in yet appears as Pending until their first sign-in, which records their directory id and activates the account."
          />

          <AccessFilters
            filters={filters}
            onChange={setFilters}
            showStatus={scope === "platform"}
          />

          <PeopleResults
            isPending={isPending}
            isError={isError}
            hasData={data !== undefined}
            error={error}
            onRetry={() => void refetch()}
            empty={people.length === 0}
            scopeName={scopeName}
            onGrant={canManage ? () => setGrantOpen(true) : undefined}
            renderGrid={() => (
              <UsersGrid
                people={people}
                scope={scope}
                names={names}
                groupAccess={groupAccess}
                mfaMode={me?.mfa?.mode}
                statusExtra={onPlatformTab ? (person) => signInCell(person, issuers) : undefined}
                rowExtra={
                  onPlatformTab && canManage && issuers.length > 0
                    ? (person) => resetSignInButton(person, setResetting)
                    : undefined
                }
                actions={{
                  canManage,
                  ...(groupAccess ? { onChangeRole: openChangeRole } : {}),
                  onRevoke: setRevoking,
                  onReactivate: (row) =>
                    setReactivating({
                      kind: "grant",
                      row,
                      scopeName: grantScopeLabel(row, names, scope),
                    }),
                  onActivateUser: (row) => setReactivating({ kind: "account", row }),
                  onDeactivateUser: setDeactivating,
                  onResendInvite: handleResendInvite,
                  busyGrantId,
                  scopeTabLabel: (target) =>
                    tabs.length > 1
                      ? (tabs.find((tab) => tab.value === target)?.label ?? null)
                      : null,
                  onOpenScope: openScope,
                }}
              />
            )}
          />
        </>
      )}

      <GrantAccessDialog
        open={isGrantOpen}
        onOpenChange={(open) => {
          setGrantOpen(open);
          if (!open) {
            grantError.clear();
            setRoleChange(null);
          }
        }}
        error={grantError.error}
        roleChange={roleChange}
        onCancelRoleChange={() => {
          grantError.clear();
          setRoleChange(null);
        }}
        scope={scope}
        grantorRole={grantorRole}
        selfEmail={me?.user.email ?? ""}
        instanceName={instanceName}
        tenantName={tenantName}
        isSubmitting={mutations.grant.isPending}
        onSubmit={handleGrant}
        emailConfigured={emailStatus.data?.emailConfigured}
        signInSetting={signInSetting.data ?? null}
        knownSignInMethod={knownSignInMethod}
        platformAdminRuleLoading={isPlatformAdministrator && issuersQuery.isPending}
        platformAdminPinned={issuers.some(
          (entry) => entry.platformAdminPin === true || entry.platformAdminWorkforceOnly === true,
        )}
        adminSignInMethods={me?.mfa?.adminSignInMethods}
        canResetSignIn={isPlatformAdministrator}
      />

      <ResetSignInDialog
        person={resetting}
        issuers={issuers}
        adminNeedsSso={me?.mfa?.adminSignInMethods !== undefined}
        isSubmitting={mutations.rebind.isPending}
        onConfirm={handleRebind}
        onOpenChange={(open) => {
          if (!open) {
            setResetting(null);
            rebindError.clear();
            setRebindIssuerIssue(null);
          }
        }}
        error={rebindError.error}
        issuerIssue={rebindIssuerIssue}
      />

      <DeactivateUserDialog
        row={deactivating}
        isSubmitting={mutations.setUserActive.isPending}
        onConfirm={handleDeactivateUser}
        onOpenChange={(open) => {
          if (!open) {
            setDeactivating(null);
            deactivateError.clear();
          }
        }}
        error={deactivateError.error}
      />

      <ChangeRoleDialog
        target={changingRole}
        grantorRole={grantorRole}
        isSubmitting={
          groupMutations.setOverride.isPending || groupMutations.clearOverride.isPending
        }
        error={changeRoleError.error}
        onConfirm={handleChangeRole}
        onOpenChange={(open) => {
          if (!open) {
            setChangingRole(null);
            changeRoleError.clear();
          }
        }}
      />

      <ReauthDialog
        open={overrideRetry.hold.isOpen}
        isRetrying={overrideRetry.hold.isRetrying}
        error={overrideRetry.hold.error}
        onConfirm={overrideRetry.hold.confirm}
        onCancel={overrideRetry.hold.cancel}
        title="Confirm your identity to change a role"
        description="Your session is still valid, but raising someone's role needs a fresh sign-in."
        heldMessage="Your change is held. If signing in again takes you to your sign-in page, make the change again when you are back."
        footnote="Nothing has changed yet. Cancelling leaves the person's role as it is."
        code={overrideRetry.hold.code}
        blocked={overrideRetry.hold.blocked}
        remember={overrideRetry.hold.remember}
      />

      <ReauthDialog
        open={accessRetry.hold.isOpen}
        isRetrying={accessRetry.hold.isRetrying}
        error={accessRetry.hold.error}
        onConfirm={accessRetry.hold.confirm}
        onCancel={accessRetry.hold.cancel}
        title="Confirm your identity to change access"
        description="Your session is still valid, but changing who can reach what needs a fresh sign-in."
        heldMessage="Your change is held. If signing in again takes you to your sign-in page, make the change again when you are back."
        footnote="Nothing has changed yet. Cancelling leaves access as it is."
        code={accessRetry.hold.code}
        blocked={accessRetry.hold.blocked}
        remember={accessRetry.hold.remember}
      />

      <ReactivateAccessDialog
        target={reactivating}
        isSubmitting={mutations.reactivate.isPending || mutations.setUserActive.isPending}
        onConfirm={handleConfirmReactivate}
        onOpenChange={(open) => {
          if (!open) {
            setReactivating(null);
            reactivateError.clear();
          }
        }}
        error={reactivateError.error}
      />

      <RevokeGrantDialog
        row={revoking}
        scopeName={revoking ? grantScopeLabel(revoking, names, scope) : scopeName}
        isSubmitting={mutations.revoke.isPending}
        onConfirm={handleRevoke}
        onOpenChange={(open) => {
          if (!open) {
            setRevoking(null);
            revokeError.clear();
          }
        }}
        error={revokeError.error}
      />
    </div>
  );
}

/**
 * Session 9 (contract §5.6): where a person's sign-in comes from. A bound row names the provider
 * it signed in through; an unbound row names the provider it is pinned to, or none.
 */
function signInLabel(
  person: AccessPerson,
  issuers: readonly AuthIssuerView[],
): { text: string; title?: string } {
  if (person.idpIssuer) {
    const via = issuerDisplay(person.idpIssuer, issuers);
    return { text: `Signed in via ${via.text}`, ...(via.id ? { title: via.id } : {}) };
  }
  // One rule with the Last active reason (`signInWait`, #1017 review).
  const wait = signInWait(person);
  // Fixes wave 3: a deactivated person who has not signed in is not waiting for a sign-in (they
  // cannot make one); the row says so, and the pin, which stays, is in the title.
  if (wait === "deactivated") {
    const text = SIGN_IN_WAIT_LABEL.deactivated;
    if (!person.expectedIdpIssuer) return { text };
    const pin = issuerDisplay(person.expectedIdpIssuer, issuers);
    return { text, title: `Pinned to ${pin.id ?? pin.text}` };
  }
  if (person.expectedIdpIssuer) {
    const via = issuerDisplay(person.expectedIdpIssuer, issuers);
    return {
      text: `${SIGN_IN_WAIT_LABEL.awaiting} via ${via.text}`,
      ...(via.id ? { title: via.id } : {}),
    };
  }
  // No binding and no sign-in on record: still waiting, not "Signed in" (review nit 2).
  if (wait === "awaiting") return { text: SIGN_IN_WAIT_LABEL.awaiting };
  return { text: "Signed in" };
}

/**
 * The platform tab's sign-in line (S9), drawn in the Status cell so the grid keeps a floor that
 * fits the 1024 px pane (#1135), and its "Reset sign-in" button, drawn in the Actions column.
 */
function signInCell(person: AccessPerson, issuers: readonly AuthIssuerView[]): ReactNode {
  const signIn = signInLabel(person, issuers);
  return (
    <span data-testid={`sign-in-${person.email}`} title={signIn.title}>
      <SkyText size={200} color="muted">
        {signIn.text}
      </SkyText>
    </span>
  );
}

function resetSignInButton(person: AccessPerson, onReset: (person: AccessPerson) => void) {
  return (
    <SkyButton
      appearance="secondary"
      size="small"
      testId={`reset-sign-in-${person.email}`}
      aria-label={`Reset sign-in for ${person.email}`}
      onClick={() => onReset(person)}
    >
      Reset sign-in
    </SkyButton>
  );
}

/** The server's reason for refusing the chosen provider (`toIssuer`), if that is what it refused. */
function toIssuerIssueOf(cause: unknown): string | null {
  if (!(cause instanceof ApiClientError)) return null;
  return cause.details.find((detail) => detail.field === "toIssuer")?.issue ?? null;
}

/** #418: the dropdown's note while a Platform admin is offered work account providers only. */
const RESET_PLATFORM_ADMIN_PROVIDER_NOTE =
  "Platform admins sign in only with a Microsoft work account.";

/** Same floor as the server's rebind body (`rebindUserBodySchema`). */
const MIN_REBIND_REASON_LENGTH = 3;

/**
 * "Reset sign-in": clears the person's binding and pins them to the chosen provider, so their
 * next sign-in through it activates the account again (contract §5.3). On your own row the
 * dialog says in words that your session ends, and sends `allowSelf`.
 */
function ResetSignInDialog({
  person,
  issuers,
  isSubmitting,
  onConfirm,
  onOpenChange,
  error,
  issuerIssue = null,
  adminNeedsSso = false,
}: {
  person: AccessPerson | null;
  issuers: readonly AuthIssuerView[];
  /**
   * #1712: the MFA mode covers admins (`GET /me` `mfa.adminSignInMethods` is present), so a person
   * holding an admin role is offered only the providers that can prove a second factor.
   */
  adminNeedsSso?: boolean;
  isSubmitting: boolean;
  onConfirm: (input: RebindInput, person: AccessPerson) => void;
  onOpenChange: (open: boolean) => void;
  /** Session 10 (ux-s10 §3): a refused rebind, said inside the dialog, which stays open. */
  error: InlineFailure | null;
  /** #418 review: the server's `toIssuer` reason, shown at the Sign-in provider dropdown. */
  issuerIssue?: string | null;
}) {
  // #418: with the workforce-only rule on (the issuer view names a `platformAdminPin`), a person
  // holding Platform admin is offered the work account providers only, the pin chosen first, as
  // the grant dialog fixes the provider for the role. The server's refusal stays the backstop.
  const platformAdminPin = issuers.find((entry) => entry.platformAdminPin === true);
  const workforceOnly =
    platformAdminPin !== undefined &&
    (person?.grants.some((grant) => grant.isActive && grant.roleName === "PlatformAdmin") ?? false);
  const ssoOnly =
    adminNeedsSso &&
    (person?.grants.some((grant) => grant.isActive && isMfaAdminRole(grant.roleName)) ?? false);
  // An older issuer view carries no `canProveMfa`: every provider stays and the server's refusal
  // is the backstop.
  const viewKnowsMfa = issuers.some((entry) => entry.canProveMfa !== undefined);
  let offered: readonly AuthIssuerView[] = issuers;
  if (workforceOnly) offered = issuers.filter((entry) => entry.claims === "entra-workforce");
  // #1712: an admin is offered only the providers that can prove a second factor.
  if (ssoOnly && viewKnowsMfa) {
    offered = offered.filter((entry) => entry.canProveMfa === true);
  }
  // #1712 review: the note names the providers as the dropdown shows them ("Microsoft"), or says
  // none can hold an admin role yet.
  const ssoNote = adminNeedsSsoChoice(
    offered.map((entry) => issuerDisplay(entry.issuer, issuers).text),
  );
  const defaultIssuer = workforceOnly
    ? platformAdminPin.issuer
    : ((offered.find((entry) => entry.isSignInIssuer) ?? offered[0])?.issuer ?? "");
  const [toIssuer, setToIssuer] = useState(defaultIssuer);
  const [reason, setReason] = useState("");
  const [submitted, setSubmitted] = useState(false);
  // The provider the last submit sent, so the server's reason shows only while it is still chosen.
  const [sentIssuer, setSentIssuer] = useState<string | null>(null);

  // Reset during render when a different person opens the dialog, as `GrantAccessDialog` does.
  const [shownFor, setShownFor] = useState<string | null>(person?.userId ?? null);
  const personId = person?.userId ?? null;
  if (shownFor !== personId) {
    setShownFor(personId);
    setToIssuer(defaultIssuer);
    setReason("");
    setSubmitted(false);
    setSentIssuer(null);
  }

  const reasonError =
    reason.trim().length < MIN_REBIND_REASON_LENGTH
      ? "Say why. The reason is recorded on the audit event."
      : undefined;
  const issuerError = toIssuer ? undefined : "Choose the provider they will sign in through.";
  const serverIssuerIssue = issuerIssue && sentIssuer === toIssuer ? issuerIssue : undefined;
  const shownIssuerError = (submitted ? issuerError : undefined) ?? serverIssuerIssue;
  // The reason now sits at the dropdown, so the bar under it does not repeat it as "toIssuer: ...".
  const barError =
    error && serverIssuerIssue
      ? {
          ...error,
          details: error.details?.filter((line) => line !== `toIssuer: ${serverIssuerIssue}`),
        }
      : error;

  function submit() {
    setSubmitted(true);
    if (!person || reasonError || issuerError) return;
    setSentIssuer(toIssuer);
    onConfirm(
      {
        userId: person.userId,
        toIssuer,
        reason: reason.trim(),
        ...(person.isSelf ? { allowSelf: true } : {}),
      },
      person,
    );
  }

  return (
    <SkyDialog
      open={person !== null}
      onOpenChange={onOpenChange}
      // The primary action never closes the dialog by itself: a missing reason keeps it open
      // with the message, and a successful rebind closes it by clearing `person`.
      onBeforeClose={(reason) => reason !== "primaryAction"}
      size="medium"
      testId="reset-sign-in-dialog"
      title={person ? `Reset sign-in for ${person.email}` : "Reset sign-in"}
      description="Their current sign-in stops working. Their next sign-in through the provider you choose activates the account again. Their access is unchanged."
      primaryAction={{
        label: person?.isSelf ? "Reset my sign-in" : "Reset sign-in",
        onClick: submit,
        loading: isSubmitting,
        destructive: true,
      }}
      secondaryAction={{ label: "Cancel", onClick: () => onOpenChange(false) }}
    >
      <div className="space-y-4">
        <SkyDropdown
          label="Sign-in provider"
          required
          testId="reset-sign-in-provider"
          selectedOptions={toIssuer ? [toIssuer] : []}
          options={offered.map((entry) => issuerOption(entry, issuers, entry.issuer))}
          validationState={shownIssuerError ? "error" : "none"}
          validationMessage={shownIssuerError}
          onOptionSelect={(values) => setToIssuer(values[0] ?? "")}
        />

        {workforceOnly ? (
          <SkyMessageBar
            intent="info"
            testId="reset-sign-in-workforce-note"
            message={RESET_PLATFORM_ADMIN_PROVIDER_NOTE}
          />
        ) : null}

        {ssoOnly && !workforceOnly ? (
          <SkyMessageBar intent="info" testId="reset-sign-in-admin-sso-note" message={ssoNote} />
        ) : null}

        <SkyInput
          label="Reason"
          required
          value={reason}
          testId="reset-sign-in-reason"
          hint="Recorded on the audit event."
          validationState={submitted && reasonError ? "error" : "none"}
          validationMessage={submitted ? reasonError : undefined}
          onChange={(event) => setReason(event.target.value)}
        />

        {person?.isSelf ? (
          <SkyMessageBar
            intent="warning"
            layout="multiline"
            testId="reset-sign-in-self-warning"
            title="This is your own account"
            message="Your session ends unless you choose the provider you are signed in with. If you choose another one, you sign in again through it, so make sure you can before you continue."
          />
        ) : null}

        <InlineErrorBar
          error={barError}
          title={error?.retryable ? undefined : "The sign-in was not reset"}
          testId="reset-sign-in-error"
        />
      </div>
    </SkyDialog>
  );
}
